import React, { useState, useEffect, useRef, useCallback, cloneElement } from 'react';
import { BookPlus, Filter, FilterX, Search, Loader, Trash2 } from 'lucide-react';
import '../css/collections.css'; // Import the CSS file for styling

function Collections() {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [sortBy, setSortBy] = useState('name'); // Set default value to 'title'
  const [searchTerm, setSearchTerm] = useState('');
  const [searchButtonText, setSearchButtonText] = useState('Search'); // Add state for search button text
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [deleteClicked, setDeleteClicked] = useState(false); // State to track delete button click
  const [isSearching, setIsSearching] = useState(false); // Add state for search button text
  const [currentPage, setCurrentPage] = useState(1);
  const [collectionsPerPage] = useState(24); // Set the number of books per page
  const [loading, setLoading] = useState(false); // Introduce loading state
  const [showFilters, setShowFilters] = useState(false); // Introduce loading state


  const isMobileDevice = () => {
    return window.innerWidth <= 768; // You can adjust the width as per your requirement
  }
  const [isMobile, setIsMobile] = useState(isMobileDevice());

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const modalOpen = Boolean((selectedCollection && !deleteClicked) || showAddModal);
    document.body.classList.toggle('modal-open', modalOpen);
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [selectedCollection, deleteClicked, showAddModal]);

  // Pagination Logic
  const indexOfLastBook = currentPage * collectionsPerPage;
  const indexOfFirstBook = indexOfLastBook - collectionsPerPage;
  const currentCollections = collections.slice(indexOfFirstBook, indexOfLastBook);
  
  // Pagination Logic
  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(collections.length / collectionsPerPage); i++) {
    pageNumbers.push(i);
  }

  const renderPageNumbers = () => {
    const maxVisiblePages = 3;
    const totalPages = pageNumbers.length;

    if (totalPages <= maxVisiblePages) {
      return pageNumbers.map(number => (
        <button
          key={number}
          onClick={() => setCurrentPage(number)}
          className={currentPage === number ? "active" : ""}
        >
          {number}
        </button>
      ));
    } else {
      let leftSide = [];
      let rightSide = [];

      if (currentPage <= maxVisiblePages - 1) {
        leftSide = pageNumbers.slice(0, maxVisiblePages);
        rightSide = ["right"];
      } else if (currentPage >= totalPages - maxVisiblePages + 1) {
        leftSide = ["left"];
        rightSide = pageNumbers.slice(totalPages - maxVisiblePages, totalPages);
      } else {
        const middleIndex = Math.max(currentPage - Math.floor(maxVisiblePages / 2), 0);
        leftSide = ["left"];
        rightSide = ["right"];

        for (let i = middleIndex; i < middleIndex + maxVisiblePages; i++) {
          leftSide.push(i);
        }
      }

      return (
        <>
          {leftSide.map((number, index) => (
            <button
              key={index}
              onClick={() => {
                if (number !== "left" && number !== "right") {
                  setCurrentPage(number);
                } else if (number === "left") {
                  setCurrentPage(currentPage - 1);
                }
                window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to the top of the page

              }}
              className={currentPage === number ? "active" : ""}
            >
              {number !== "left" && number !== "right" ? number : "←"}
            </button>
          ))}
          {rightSide.map((number, index) => (
            <button
              key={index + leftSide.length}
              onClick={() => {
                if (number !== "left" && number !== "right") {
                  setCurrentPage(number);
                } else if (number === "right") {
                  setCurrentPage(currentPage + 1);
                }
                window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to the top of the page
              }}
              className={currentPage === number ? "active" : ""}
            >
              {number !== "left" && number !== "right" ? number : "→"}
            </button>
          ))}
        </>
      );
    }
  };
  
  const getAllCollections = async () => {
    setLoading(true);

    const user = localStorage.getItem('loggedInUsername');
    const token = localStorage.getItem('accessToken');

    try {
      const response = await fetch("/api/library/collections", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ user, sortBy }),
      });
      if (response.ok) {
        const data = await response.json();
        setCollections(data);
      } else {
        console.error('Login or registration failed');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllCollections();
  }, [sortBy]);

  const handleSearch = async () => {
    const user = localStorage.getItem('loggedInUsername');
    const token = localStorage.getItem('accessToken');

    try {
      const response = await fetch("/api/library/collection/search", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ searchTerm, user }),
      });
      if (response.ok) {
        const data = await response.json();
        setCollections(data);
      } else {
        console.error('Search failed');
      }
       
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      document.getElementById('searchButton').click();
    }
  };

  const handleCollectionClick = async(collection) => {
    const user = localStorage.getItem('loggedInUsername');
    const token = localStorage.getItem('accessToken');

      try {
        const response = await fetch(`/api/library/collection/books`, {
            method: "POST",
            headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ user, collectionId: collection.id }),
        });
        if (response.ok) {
            const data = await response.json();
            setSelectedCollection({ ...collection, books: data.books });
        } else {
            console.error('Failed to fetch books for the collection');
        }
        } catch (error) {
        console.error('Error:', error);
        }
    };

  const handleCloseCollectionDetails = () => {
    setSelectedCollection(null);
    if(searchTerm.length > 0){
      handleSearch();
    }else{
      getAllCollections();
    }
  };

  // Swipe-down-to-close state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const dragStartY = useRef(0);
  const modalRef = useRef(null);
  const wasOpen = useRef(false);
  const CLOSE_THRESHOLD = 120;

  useEffect(() => {
    const isOpen = selectedCollection && !deleteClicked;
    if (isOpen && !wasOpen.current) {
      setIsOpening(true);
      const frame = requestAnimationFrame(() => setIsOpening(false));
      wasOpen.current = true;
      return () => cancelAnimationFrame(frame);
    }
    wasOpen.current = !!isOpen;
  }, [selectedCollection, deleteClicked]);

  const handleTouchStart = useCallback((e) => {
    const modal = modalRef.current;
    if (modal && modal.scrollTop > 0) return;
    dragStartY.current = e.touches[0].clientY;
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const delta = Math.max(0, e.touches[0].clientY - dragStartY.current);
    setDragY(delta);
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragY > CLOSE_THRESHOLD) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        setDragY(0);
        handleCloseCollectionDetails();
      }, 300);
    } else {
      setDragY(0);
    }
  }, [isDragging, dragY, handleCloseCollectionDetails]);

  const collectionModalStyle = isClosing
    ? { transform: 'translateY(100%)', transition: 'transform 0.3s cubic-bezier(0.2, 0, 0, 1)' }
    : isDragging
      ? { transform: `translateY(${dragY}px)`, transition: 'none' }
      : isOpening
        ? { transform: 'translateY(100%)' }
        : { transform: 'translateY(0)', transition: 'transform 0.3s cubic-bezier(0.2, 0, 0, 1)' };

  const toggleSearchButtonText = () => {
    setSearchButtonText(searchButtonText === 'Search' ? 'Clear Search' : 'Search');
  };

  const handleAddModalClose = () => {
    setShowAddModal(false);
    setNewCollectionName('');
  };

  const READ_STATE = {
    0: "READ",
    1: "UNREAD",
    2: "READING"
  };
  
  const OWNERSHIP_STATE = {
    0: "OWNED",
    1: "ORDERED",
    2: "WANTED"
  };
  const getStatusStyle = (value) => {
    switch(value) {
      case READ_STATE[0]:
        return { backgroundColor: '#459948', color: '#fdfbff' };
      case READ_STATE[1]:
        return { backgroundColor: '#d83f92', color: '#fdfbff' };
      case READ_STATE[2]:
        return { backgroundColor: '#6fc3e5', color: '#1a0d1f' };
      case OWNERSHIP_STATE[0]:
        return { backgroundColor: '#d3d3e5', color: '#1a0d1f' };
      case OWNERSHIP_STATE[1]:
        return { backgroundColor: '#e398d0', color: '#1a0d1f' };
      case OWNERSHIP_STATE[2]:
        return { backgroundColor: '#e6e684', color: '#1a0d1f' };
      default:
        return { backgroundColor: '#6fc3e5', color: '#1a0d1f' };
    }
  };

  const handleAddCollection = async () => {
    const user = localStorage.getItem('loggedInUsername');
    const token = localStorage.getItem('accessToken');

    try {
      const response = await fetch("/api/library/collections/add", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ name: newCollectionName,
                               owner: user,
                               timestamp: Math.floor(Date.now() / 1000) }),
      });
      if (response.ok) {
        // Refresh collections after adding
        getAllCollections();
        setShowAddModal(false);
        setNewCollectionName('');
      } else {
        console.error('Failed to add collection');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const deleteCollection = async (collectionId) => {
    setSelectedCollection(null);
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('loggedInUsername');

    try {
      const response = await fetch("/api/library/collections/delete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ collectionId, user }),
      });
      if (response.ok) {
        // Collection deleted successfully, update UI or perform any necessary actions
        getAllCollections(); // Refresh collections after deletion
      } else {
        console.error('Failed to delete collection');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const deleteBookFromCollection = async (collectionId, bookId) => {
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('loggedInUsername');

    try {
      const response = await fetch("/api/library/collection/delete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`

        },
        body: JSON.stringify({ bookId, collectionId, user }),
      });
      if (response.ok) {
        setSelectedCollection(prevState => ({
          ...prevState,
          books: prevState.books.filter(book => book.id !== bookId)
        }));
      } else {
        console.error('Failed to delete book from collection');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleClearSearch = async() => {
    setIsSearching(false);
    setSearchTerm('');
    getAllCollections();
  }

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  }

  return (
    <div className='lib-page'>
      <div className="search-toolbar">
        <div className="search-toolbar-row">
          <button className='search-button-3' onClick={() => setShowAddModal(true)}><BookPlus size={18} /></button>
          <button className='search-button-3' onClick={toggleFilters}>{showFilters ? <FilterX size={18} /> : <Filter size={18} />}</button>
          <button id="searchButton" className="search-button-3" onClick={() => { setCurrentPage(1); setIsSearching(true); handleSearch(); }}><Search size={18} /></button>
          <input
            id="search-box"
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown} // Add key press event listener
            className='search-box'
          />
          <div className="pagination search-pagination">
            {renderPageNumbers()}
          </div>
          {isSearching && (
            <button id="clearSearchButton" className="search-button search-clear" onClick={() => { handleClearSearch(); }}>Clear</button>
          )}
        </div>
      </div>

      <div>
        {showFilters && (
          <div className="filter-panel filter-panel-bottom">
            <div className="select-container">
              <label className='select-label'>Sort by:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className='sort-by-2'>
                <option value="name">Name</option>
                <option value="newest">Newest</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-container">
          <Loader size={32} className="spin" />
        </div>
      )} {/* Display loading message when loading is true */}


      <table className="custom-table">
        <tbody>
          <tr>
            {currentCollections.map((collection, index) => {
              // Parse the imageLinks string into a JSON object
              let imageLinks = ""
              const image = collection.image;
              if(collection.image[0] !== "d" && collection.image !== ""){
                imageLinks = JSON.parse(collection.image)
              }

              return (
                <td key={index}>
                  <div className="book-item" onClick={() => handleCollectionClick(collection)}>

                    { (imageLinks && imageLinks.thumbnail) ? (
                      <img className="book-thumbnail" src={imageLinks.thumbnail} />
                    ) : image != "" ? (
                      <img className="book-thumbnail" src={image} />
                    ) : null}

                    <p className='name'>{collection.name} </p>
                    <div  style={{ textAlign: 'center' }}>
                        <button className="search-button-3 black" onClick={() => { deleteCollection(collection.id); setDeleteClicked(true);} }><Trash2 size={16} /></button>
                    </div>
                  </div>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>

      <div className="pagination">
        {renderPageNumbers()}
      </div>
        
      {(selectedCollection && !deleteClicked) && (
        <div className="book-details-modal" onClick={handleCloseCollectionDetails}>
          <article
            className="modal-content"
            ref={modalRef}
            style={collectionModalStyle}
            onClick={(event) => event.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
                <div className="modal-drag-handle" aria-hidden="true">
                  <span className="modal-drag-bar"></span>
                </div>
                <h2>{selectedCollection.name}</h2>
                <div className='modal-content-scroll'>
                <div className="books-container">
                    {selectedCollection.books.map((book, index) => (
                        <div key={index} className="book-item-collection">
                            <div className='margin_45_left_right'>
                            {book.imageLinks != "{}" && JSON.parse(book.imageLinks) ? (
                              <img className="book-thumbnail" src={JSON.parse(book.imageLinks).thumbnail} alt={book.title} />
                            ) : book.uploaded_image_b64 != "" ? (
                              <img className="book-thumbnail" src={book.uploaded_image_b64} alt={book.title} />
                            ) : <img className="book-thumbnail" src={JSON.parse(book.imageLinks).thumbnail} alt={book.title} />}
                            </div>
                            <div className='margin_45_left_right'>
                              <p className="book-title-collection">{book.title}</p>
                              <div style={{
                                  ...getStatusStyle(book.read),
                                  padding: '1px 10px',
                                  borderRadius: '5px'
                                }}>
                                {book.read}
                              </div>
                              <div style={{
                                  ...getStatusStyle(book.ownership),
                                  padding: '1px 10px',
                                  borderRadius: '5px',
                                  marginTop: '5px'
                                }}>
                                {book.ownership}
                              </div>

                            </div>
                            
                            <div className='margin_45_left_right'>
                              <button className="delete-button" onClick={() => deleteBookFromCollection(selectedCollection.id, book.id)}>Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
              </div>
            </article>
        </div>
    )}
      {showAddModal && (
        <div className="book-details-modal" onClick={handleAddModalClose}>
          <article className="modal-content" onClick={(event) => event.stopPropagation()}>
            <h2>Add Collection</h2>
            
            <input
              type="text"
              className='search-box-add'
              placeholder="Collection Name"
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e.target.value)}
            />
            <button className="modal-button-add" onClick={handleAddCollection}>Add Collection</button>
          </article>
        </div>
      )}
    </div>
  );
}

export default Collections;
